import {
  AnchorButton,
  Button,
  Callout,
  Classes,
  Divider,
  Drawer,
  Icon,
  Intent,
  Label,
  PopoverPosition,
  Position,
  Pre,
  Tab,
  Tabs,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { IToken } from 'chevrotain';
import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import {
  QueryBuilderContext,
  QueryBuilderContextValue,
  QueryBuilderModelContext,
  QueryBuilderSchemaContext,
  QueryTokens,
  Text,
  Tooltip,
  TopExpressionBlock,
} from 'vet-bones/components';
import { getLocalName, isEqual, isNotEqual } from 'vet-bones/utils';
import {
  CURRENT_QUERY_BUILDER_VERSION,
  DraftTopExpression,
  QueryBuilder,
  QueryBuilderModel,
  QueryBuilderSchema,
  TopExpression,
  Variable,
  addDraftFields,
  createNewTopExpression,
  getUsedVariables,
  removeDraftFields,
  srs,
  updateValidation,
} from 'vet-bones/utils/queryBuilder';
import { SelectItem } from 'vet-bones/utils/select';

import { EditableLabel } from 'src/components/EditableLabel';
import { createNewQueryBuilderClassItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { useCreateQueryBuilderClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  ModelRule,
  ModelRuleType,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/addModelConceptHierarchy';
import { getProjectModelIdsForIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { addRule } from 'src/containers/ProjectDiagram/utils/rules/addRule';
import { getValidThenModel } from 'src/containers/ProjectDiagram/utils/rules/getValidThenModel';
import { removeRule } from 'src/containers/ProjectDiagram/utils/rules/removeRule';
import { updateRule } from 'src/containers/ProjectDiagram/utils/rules/updateRule';
import {
  IF_QUERY_BUILDER_SCHEMA,
  THEN_QUERY_BUILDER_SCHEMA,
} from 'src/containers/ProjectRuleDetailDrawer/constants';
import { ProjectRuleTextEditor } from 'src/containers/ProjectRuleDetailDrawer/ProjectRuleTextEditor';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { projectRuleModelSelector } from 'src/selectors/projectRuleModel';
import { projectRuleSelector } from 'src/selectors/projectRules';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { RDFS_LABEL_IRI } from 'src/templates/queries/iris';
import { getAllProjectNamespaces } from 'src/utils/export/namespaces';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { stringifyQueryBuilderRule } from 'src/utils/stringify/rules/rules';

function setStateIfNotEqual<T>(next: T): React.SetStateAction<T> {
  return (prev) => {
    if (isEqual(prev, next)) {
      return prev;
    }
    return next;
  };
}

enum RuleDetailDrawerTab {
  RULE_BUILDER = 'RULE_BUILDER',
  TEXT_EDITOR = 'TEXT_EDITOR',
}

const removeNewClassesAndDraftFields = (
  draft: DraftTopExpression,
  replaceVariables?: boolean
) => {
  const newClassesByIri: Record<
    string,
    { iri: string; label: string; parentIris: Set<string> }
  > = {};

  // for comparing query builder changes, ignore the randomly generated uuid
  let currVariableIdx = 0;
  const replacedVariableIds: Record<string, string> = {};
  const replaceVariableId = <T extends string | Variable | null | undefined>(
    variable: T
  ): T => {
    if (!variable || typeof variable !== 'object') {
      return variable;
    }

    if (!replacedVariableIds[variable.id]) {
      currVariableIdx += 1;
      replacedVariableIds[variable.id] = `${currVariableIdx}`;
    }

    return { ...variable, id: replacedVariableIds[variable.id] };
  };

  const topExpression = removeDraftFields({
    ...draft,
    expressions: draft.expressions.map((subExpression) => ({
      ...subExpression,
      expressions: subExpression.expressions.map((expression) => ({
        ...expression,
        filters: expression.filters.map((filter) => {
          let { subject, comparisonSubject, value } = filter;
          if (replaceVariables) {
            subject = replaceVariableId(filter.subject);
            comparisonSubject = replaceVariableId(filter.comparisonSubject);
            value = replaceVariableId(filter.value);
          }

          if (value && typeof value === 'object' && value.isNewlyCreated) {
            if (!newClassesByIri[value.iri]) {
              newClassesByIri[value.iri] = {
                iri: value.iri,
                label: value.newLabel || getLocalName(value.iri),
                parentIris: new Set(),
              };
            }

            if (subject?.iri) {
              newClassesByIri[value.iri].parentIris.add(subject.iri);
            }

            return {
              ...filter,
              subject,
              comparisonSubject,
              value: { id: value.id, iri: value.iri },
            };
          }
          return filter;
        }),
      })),
    })),
  });
  return { newClassesByIri, topExpression };
};

/**
 * Converts saved query builder rule data into draft values
 *
 * @returns draft values, whether the values are valid, variables
 */
const getQueryBuilderValues = (
  queryBuilderIf: TopExpression | null,
  queryBuilderThen: TopExpression | null,
  validModel: QueryBuilderModel,
  validThenModel: QueryBuilderModel
) => {
  const { isValid: ifValid, topExpression: ifValue } = queryBuilderIf
    ? updateValidation({
        copy: IF_QUERY_BUILDER_SCHEMA.copy,
        topExpression: addDraftFields(queryBuilderIf),
        validModel,
      })
    : { isValid: true, topExpression: createNewTopExpression() };
  const ifVariables = getUsedVariables(ifValue);

  const { isValid: thenValid, topExpression: thenValue } = queryBuilderThen
    ? updateValidation({
        copy: THEN_QUERY_BUILDER_SCHEMA.copy,
        topExpression: addDraftFields(queryBuilderThen),
        validModel: validThenModel,
        validVariables: ifVariables,
      })
    : { isValid: true, topExpression: createNewTopExpression() };
  const thenVariables = getUsedVariables(thenValue);

  return {
    ifValue,
    ifValid,
    ifVariables,
    thenValue,
    thenValid,
    thenVariables,
    showValidation: !ifValid || !thenValid,
  };
};

/**
 * Converts draft query builder rule data into text
 *
 * @returns string | null (in the case of an error)
 */
const getTextValues = (
  ifValue: DraftTopExpression,
  thenValue: DraftTopExpression
) => {
  try {
    const texts = stringifyQueryBuilderRule(
      { id: '', name: '' },
      ifValue,
      thenValue
    ).ruleContents;
    return texts;
  } catch (err) {
    console.debug(err);
    return null;
  }
};

/**
 * Computes the local data for a provided rule
 *
 * @returns query builder values, text values, score
 * score = 1 means the query is fully converted between formats
 * score = null means the query cannot be converted between formats
 */
const useInitialRuleData = (
  existingRule: ModelRule | null,
  validModel: QueryBuilderModel,
  validThenModel: QueryBuilderModel,
  namespaces: Record<string, string>
) =>
  React.useMemo(() => {
    let score: number | null = 1;
    let textTokens: {
      all: IToken[];
      ignored: IToken[];
      unprocessed: IToken[];
    } | null = null;
    let textValue = '';
    let queryBuilderIf: TopExpression | null = null;
    let queryBuilderThen: TopExpression | null = null;

    if (
      existingRule?.type === ModelRuleType.QUERY_BUILDER &&
      existingRule.ifClause &&
      existingRule.thenClause
    ) {
      queryBuilderIf = existingRule.ifClause.data.query;
      queryBuilderThen = existingRule.thenClause.data.query;

      // compute the text for a query builder rule
      const texts = getTextValues(
        addDraftFields(queryBuilderIf),
        addDraftFields(queryBuilderThen)
      );
      if (texts?.length === 1) {
        // query builder can be fully converted into a single if/then block
        score = 1;
        [textValue] = texts;
      } else if (texts?.length) {
        // query builder cannot be converted using one if/then block
        score = null;
      } else {
        // parsing failed
        score = 0;
      }
    } else if (existingRule) {
      textValue = existingRule.text || '';

      // compute the query builder for a text rule
      const parsed = srs(existingRule.text || '', validThenModel, namespaces, {
        stardogLabelIri: RDFS_LABEL_IRI,
      });
      if (parsed?.rules.length === 1) {
        // text can be fully converted into a single if/then block
        score = parsed.score;
        textTokens = parsed.tokens;
        queryBuilderIf = parsed.rules[0].ifTopExpression;
        queryBuilderThen = parsed.rules[0].thenTopExpression;
      } else if (parsed?.rules.length) {
        // text cannot be converted using one if/then block
        score = null;
      } else {
        // parsing failed
        score = 0;
      }
    }

    const queryBuilderValues = getQueryBuilderValues(
      queryBuilderIf,
      queryBuilderThen,
      validModel,
      validThenModel
    );

    return {
      ...queryBuilderValues,
      showValidation:
        queryBuilderIf &&
        queryBuilderThen &&
        (!queryBuilderValues.ifValid || !queryBuilderValues.thenValid),
      score,
      textTokens,
      textValue,
    };
  }, [existingRule, namespaces, validModel, validThenModel]);

const ProjectRuleDetailDrawerBody = () => {
  const { projectId } = useRouterParams();

  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const targetModel = useReduxSelector(projectTargetModelIdSelector(projectId));
  const allModelItems = useReduxSelector(
    projectModelItemsSelector(projectId),
    isEqual
  );
  const existingRuleId = useReduxSelector(
    (state) => state.ui.ruleDetailDrawer.ruleId || ''
  );
  const existingRule = useReduxSelector(
    projectRuleSelector(projectId, existingRuleId)
  );
  const didExistingRuleChange = useDidValueChange(existingRule, isEqual);

  const validModel = useReduxSelector(
    projectRuleModelSelector(projectId),
    isEqual
  );

  const validThenModel = React.useMemo(
    () => getValidThenModel(validModel),
    [validModel]
  );

  const modelsById = useReduxSelector(projectModelsByIdSelector(projectId));
  const namespaces = getAllProjectNamespaces(modelsById);

  const [name, setName] = React.useState(existingRule?.name || '');
  const [didEditName, setDidEditName] = React.useState(false);
  const [modelIds, setModelIds] = React.useState<string[]>(
    existingRule?.models || [targetModel]
  );

  const initialData = useInitialRuleData(
    existingRule,
    validModel,
    validThenModel,
    namespaces
  );

  const [localIfValue, setLocalIfValue] = React.useState(initialData.ifValue);
  const [ifVariables, setIfVariables] = React.useState(initialData.ifVariables);

  const [localThenValue, setLocalThenValue] = React.useState(
    initialData.thenValue
  );
  const [thenVariables, setThenVariables] = React.useState(
    initialData.thenVariables
  );
  const [showValidation, setShowValidation] = React.useState(
    initialData.showValidation
  );
  const [localScore, setLocalScore] = React.useState(initialData.score);
  const [localTextTokens, setLocalTextTokens] = React.useState<{
    all: IToken[];
    ignored: IToken[];
    unprocessed: IToken[];
  } | null>(initialData.textTokens);
  const [localTextValue, setLocalTextValue] = React.useState(
    initialData.textValue
  );

  const [selectedTab, setSelectedTab] = React.useState<RuleDetailDrawerTab>(
    () =>
      initialData.score === 1
        ? RuleDetailDrawerTab.RULE_BUILDER
        : RuleDetailDrawerTab.TEXT_EDITOR
  );

  React.useEffect(() => {
    if (didExistingRuleChange) {
      setName(existingRule?.name || '');
      setDidEditName(false);
      setModelIds(existingRule?.models || [targetModel]);

      setLocalIfValue(initialData.ifValue);
      setIfVariables(initialData.ifVariables);
      setLocalThenValue(initialData.thenValue);
      setThenVariables(initialData.thenVariables);
      setShowValidation(initialData.showValidation);

      setLocalScore(initialData.score);
      setLocalTextTokens(initialData.textTokens);
      setLocalTextValue(initialData.textValue);
    }
  }, [didExistingRuleChange, existingRule, initialData, targetModel]);

  const handleSetName = (newName: string) => {
    setName(newName);
  };

  const handleBlur = (newValue: string) => {
    if (existingRule ? newValue !== existingRule.name : newValue === '') {
      setDidEditName(true);
    }
  };

  const handleValidateName = React.useCallback(
    (name: string) => {
      if (!didEditName) {
        return '';
      }

      return name.trim()
        ? ''
        : copy.components.projectRuleDetailDrawer.validation.nameRequired;
    },
    [didEditName]
  );

  const setThenValue = React.useCallback(
    (nextTopExpression: DraftTopExpression) => {
      setLocalThenValue(setStateIfNotEqual(nextTopExpression));
      const nextThenValues = getUsedVariables(nextTopExpression);
      setThenVariables(setStateIfNotEqual(nextThenValues));
      const texts = getTextValues(localIfValue, nextTopExpression);
      if (texts?.length === 1) {
        // query builder can be fully converted into a single if/then block
        setLocalScore(1);
        setLocalTextTokens(null);
        setLocalTextValue(texts[0]);
      } else {
        // query builder cannot be converted using one if/then block
        setLocalScore(texts?.length ? null : 0);
        setLocalTextTokens(null);
        setLocalTextValue('');
      }
    },
    [localIfValue]
  );

  const handleThenValidation = React.useCallback(
    ({
      nextTopExpression,
      validVariables,
      skipOperator,
    }: {
      nextTopExpression: DraftTopExpression;
      validVariables: Record<string, string[]>;
      skipOperator?: boolean;
    }) => {
      const { topExpression, isValid } = updateValidation({
        copy: THEN_QUERY_BUILDER_SCHEMA.copy,
        topExpression: nextTopExpression,
        skipOperator,
        validModel: validThenModel,
        validVariables,
      });
      setThenValue(topExpression);
      return { topExpression, isValid };
    },
    [setThenValue, validThenModel]
  );

  const handleSetThen = React.useCallback(
    (nextTopExpression: DraftTopExpression, validate?: boolean) => {
      if (validate) {
        handleThenValidation({
          nextTopExpression,
          validVariables: ifVariables,
          skipOperator: true,
        });
      } else {
        setThenValue(nextTopExpression);
      }
    },
    [handleThenValidation, ifVariables, setThenValue]
  );

  const setIfValue = React.useCallback(
    (nextTopExpression: DraftTopExpression) => {
      setLocalIfValue(setStateIfNotEqual(nextTopExpression));
      const nextIfVariables = getUsedVariables(nextTopExpression);
      setIfVariables(setStateIfNotEqual(nextIfVariables));
      const texts = getTextValues(nextTopExpression, localThenValue);
      if (texts?.length === 1) {
        // query builder can be fully converted into a single if/then block
        setLocalScore(1);
        setLocalTextTokens(null);
        setLocalTextValue(texts[0]);
      } else {
        // query builder cannot be converted using one if/then block
        setLocalScore(texts?.length ? null : 0);
        setLocalTextTokens(null);
        setLocalTextValue('');
      }
    },
    [localThenValue]
  );

  const handleIfValidation = React.useCallback(
    ({
      nextTopExpression,
      skipOperator,
    }: {
      nextTopExpression: DraftTopExpression;
      skipOperator?: boolean;
    }) => {
      const { topExpression, isValid } = updateValidation({
        copy: IF_QUERY_BUILDER_SCHEMA.copy,
        topExpression: nextTopExpression,
        skipOperator,
        validModel,
      });
      setIfValue(topExpression);
      return { topExpression, isValid };
    },
    [setIfValue, validModel]
  );

  const handleSetIf = React.useCallback(
    (nextTopExpression: DraftTopExpression, validate?: boolean) => {
      if (validate) {
        handleIfValidation({ nextTopExpression, skipOperator: true });
      } else {
        setIfValue(nextTopExpression);
      }
    },
    [handleIfValidation, setIfValue]
  );

  const setTextValue = React.useCallback(
    (nextText: string) => {
      setLocalTextValue(nextText);
      const parsed = srs(nextText || '', validThenModel, namespaces, {
        stardogLabelIri: RDFS_LABEL_IRI,
      });
      if (parsed?.rules.length === 1) {
        // text can be fully converted into a single if/then block
        setLocalScore(parsed.score);
        setLocalTextTokens(parsed.tokens);

        const nextValues = getQueryBuilderValues(
          parsed.rules[0].ifTopExpression,
          parsed.rules[0].thenTopExpression,
          validModel,
          validThenModel
        );
        setLocalIfValue(nextValues.ifValue);
        setIfVariables(nextValues.ifVariables);
        setLocalThenValue(nextValues.thenValue);
        setThenVariables(nextValues.thenVariables);
        setShowValidation(false);
      } else {
        // text cannot be converted using one if/then block
        setLocalScore(parsed?.rules.length ? null : 0);
        setLocalTextTokens(null);

        setLocalIfValue(createNewTopExpression());
        setIfVariables({});
        setLocalThenValue(createNewTopExpression());
        setThenVariables({});
        setShowValidation(false);
      }
    },
    [namespaces, validModel, validThenModel]
  );

  const validateRuleBuilder = React.useCallback(
    (shouldShowValidation: boolean) => {
      const ifClauseValidation = handleIfValidation({
        nextTopExpression: localIfValue,
      });
      const thenClauseValidation = handleThenValidation({
        nextTopExpression: localThenValue,
        validVariables: ifVariables,
      });

      if (!ifClauseValidation.isValid || !thenClauseValidation.isValid) {
        if (shouldShowValidation) {
          setShowValidation(true);
        }
        return null;
      }

      const ifClauseData: QueryBuilder = {
        version: CURRENT_QUERY_BUILDER_VERSION,
        data: {
          query: removeNewClassesAndDraftFields(localIfValue).topExpression,
          variableIds: [],
        },
      };

      const {
        newClassesByIri: newThenClassesByIri,
        topExpression: thenQueryData,
      } = removeNewClassesAndDraftFields(localThenValue);

      const thenClauseData: QueryBuilder = {
        version: CURRENT_QUERY_BUILDER_VERSION,
        data: { query: thenQueryData, variableIds: [] },
      };

      return {
        ifClauseData,
        newThenClassesByIri,
        thenClauseData,
      };
    },
    [
      handleIfValidation,
      handleThenValidation,
      ifVariables,
      localIfValue,
      localThenValue,
    ]
  );

  const onClose = () => {
    setName('');
    setDidEditName(false);

    setLocalIfValue(createNewTopExpression());
    setIfVariables({});
    setLocalThenValue(createNewTopExpression());
    setThenVariables({});
    setShowValidation(false);

    setLocalScore(0);
    setLocalTextTokens(null);
    setLocalTextValue('');

    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: false }));
  };

  const onSubmit = () => {
    if (!diagram) {
      return;
    }

    // run validation on query builder
    const info = validateRuleBuilder(true);

    if (selectedTab === RuleDetailDrawerTab.RULE_BUILDER) {
      if (!info) {
        // do not submit invalid query builder rules
        return;
      }

      const addNewThenClasses = (model: Model) => {
        Object.values(info.newThenClassesByIri).forEach(
          ({ iri, label, parentIris }) => {
            const projectModelIds = new Set<string>();
            parentIris.forEach((parentIri) => {
              getProjectModelIdsForIri(parentIri, model).forEach((modelId) => {
                projectModelIds.add(modelId);
              });
            });

            addModelConceptClass({
              iri,
              label,
              projectModelIds: Array.from(projectModelIds),
            })(model);

            parentIris.forEach((parentIri) => {
              addModelConceptHierarchy({ fromIri: iri, toIri: parentIri })(
                model
              );
            });
          }
        );
      };

      if (existingRuleId) {
        commitDiagramModel(
          diagram,
          (model) => {
            addNewThenClasses(model);
            updateRule({
              id: existingRuleId,
              models: modelIds,
              name,
              ifClause: info.ifClauseData,
              thenClause: info.thenClauseData,
              // no need to attatch text for query builder rules
              text: '',
              type: ModelRuleType.QUERY_BUILDER,
            })(model);
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      } else {
        commitDiagramModel(
          diagram,
          (model) => {
            addNewThenClasses(model);
            addRule({
              id: uuid(),
              models: modelIds,
              name,
              ifClause: info.ifClauseData,
              thenClause: info.thenClauseData,
              // no need to attatch text for query builder rules
              text: '',
              type: ModelRuleType.QUERY_BUILDER,
            })(model);
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      }
    } else if (selectedTab === RuleDetailDrawerTab.TEXT_EDITOR) {
      if (!localTextValue.trim()) {
        // do not submit empty text rules
        return;
      }

      if (existingRuleId) {
        commitDiagramModel(
          diagram,
          (model) => {
            updateRule({
              id: existingRuleId,
              models: modelIds,
              name,
              // attach query builder objects for filters
              ifClause: info?.ifClauseData || null,
              thenClause: info?.thenClauseData || null,
              text: localTextValue,
              type: ModelRuleType.TEXT,
            })(model);
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      } else {
        commitDiagramModel(
          diagram,
          (model) => {
            addRule({
              id: uuid(),
              models: modelIds,
              name,
              // attach query builder objects for filters
              ifClause: info?.ifClauseData || null,
              thenClause: info?.thenClauseData || null,
              text: localTextValue,
              type: ModelRuleType.TEXT,
            })(model);
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      }
    }

    onClose();
  };

  const ifCtx = React.useMemo(
    (): QueryBuilderContextValue => ({
      topExpression: localIfValue,
      otherVariables: thenVariables,
    }),
    [localIfValue, thenVariables]
  );

  const thenCtx = React.useMemo(
    (): QueryBuilderContextValue => ({
      topExpression: localThenValue,
      validVariables: ifVariables,
      otherVariables: ifVariables,
    }),
    [localThenValue, ifVariables]
  );

  const handleDelete = () => {
    if (!diagram || !existingRule) {
      return;
    }
    commitDiagramModel(diagram, removeRule(existingRule.id));
    onClose();
  };

  const handleAddModel = (model: SelectItem | null) => {
    if (!model) {
      return;
    }
    setModelIds([...modelIds, model?.value]);
  };

  const handleRemoveModel = (model: SelectItem) => {
    setModelIds(modelIds.filter((id) => id !== model.value));
  };

  const selectedModelSelectItems = React.useMemo(
    () => allModelItems.filter((m) => modelIds.includes(m.value)),
    [allModelItems, modelIds]
  );

  const availableModels: SelectItem[] = React.useMemo(
    () => allModelItems.filter((m) => !modelIds.includes(m.value)),
    [allModelItems, modelIds]
  );

  const showRuleBuilderWarning = Boolean(
    (localTextTokens || localTextValue) &&
      selectedTab === RuleDetailDrawerTab.RULE_BUILDER &&
      localScore !== null &&
      localScore !== 1
  );

  const saveButtonError = React.useMemo(() => {
    if (!name.trim()) {
      return copy.components.projectRuleDetailDrawer.validation.nameRequired;
    }

    return '';
  }, [name]);

  // disabled if any section is empty or unmodified vs previous state
  const isSaveEnabled = React.useMemo(() => {
    if (saveButtonError) {
      return false;
    }

    let hasChanges = false;
    if (selectedTab === RuleDetailDrawerTab.RULE_BUILDER) {
      if (showRuleBuilderWarning) {
        // cannot save unedited invalid text query
        return false;
      }

      if (
        isEqual(
          removeNewClassesAndDraftFields(localIfValue, true),
          removeNewClassesAndDraftFields(createNewTopExpression(), true)
        ) ||
        isEqual(
          removeNewClassesAndDraftFields(localThenValue, true),
          removeNewClassesAndDraftFields(createNewTopExpression(), true)
        )
      ) {
        // local query builder is empty
        return false;
      }

      if (existingRule) {
        // local query builder is different than existing query builder
        hasChanges =
          isNotEqual(
            removeNewClassesAndDraftFields(localIfValue, true).topExpression,
            removeNewClassesAndDraftFields(initialData.ifValue, true)
              .topExpression
          ) ||
          isNotEqual(
            removeNewClassesAndDraftFields(localThenValue, true).topExpression,
            removeNewClassesAndDraftFields(initialData.thenValue, true)
              .topExpression
          );
      } else {
        // brand new query builder
        hasChanges = true;
      }
    }
    if (selectedTab === RuleDetailDrawerTab.TEXT_EDITOR) {
      // local text is empty
      if (!localTextValue.trim()) {
        return false;
      }

      if (existingRule) {
        // local text is different than existing text
        hasChanges = localTextValue.trim() !== initialData.textValue.trim();
      } else {
        // brand new text
        hasChanges = true;
      }
    }

    hasChanges =
      hasChanges ||
      // no content changes, but name has changed
      Boolean(
        existingRule &&
          (isNotEqual(name, existingRule.name) ||
            isNotEqual(modelIds, existingRule.models))
      );

    return hasChanges;
  }, [
    existingRule,
    initialData,
    localIfValue,
    localTextValue,
    localThenValue,
    modelIds,
    name,
    saveButtonError,
    selectedTab,
    showRuleBuilderWarning,
  ]);

  const ruleBuilderTabTooltip = React.useMemo(() => {
    if (selectedTab === RuleDetailDrawerTab.RULE_BUILDER) {
      return '';
    }

    // missing score indicates unsupported conversion
    if (localScore === null) {
      return copy.components.projectRuleDetailDrawer
        .ruleBuilderMultipleRulesError;
    }

    return '';
  }, [localScore, selectedTab]);

  const textTabTooltip = React.useMemo(() => {
    if (selectedTab === RuleDetailDrawerTab.TEXT_EDITOR) {
      return '';
    }

    // missing score indicates unsupported conversion
    if (localScore === null) {
      return copy.components.projectRuleDetailDrawer
        .textEditorMultipleRulesError;
    }

    return '';
  }, [localScore, selectedTab]);

  const allNewIris = React.useMemo(() => {
    const iris = new Set<string>();
    localThenValue.expressions.forEach((subExpression) =>
      subExpression.expressions.forEach((expression) =>
        expression.filters.forEach((filter) => {
          if (
            typeof filter.value !== 'string' &&
            filter.value?.isNewlyCreated
          ) {
            iris.add(filter.value.iri);
          }
        })
      )
    );
    return iris;
  }, [localThenValue]);

  const createNewClassFromQuery =
    useCreateQueryBuilderClassFromQuery(allNewIris);

  const thenSchema = React.useMemo<QueryBuilderSchema>(
    () => ({
      ...THEN_QUERY_BUILDER_SCHEMA,
      creatableClassProps: {
        createNewItemFromQuery: createNewClassFromQuery,
        createNewItemRenderer: createNewQueryBuilderClassItemRenderer,
      },
    }),
    [createNewClassFromQuery]
  );

  const showReasoningWarning = React.useMemo(
    () =>
      localIfValue.expressions.some(
        (subExpression) => subExpression.expressions.length > 1
      ),
    [localIfValue]
  );

  const queryBuilder = (
    <>
      {showRuleBuilderWarning ? (
        <Tooltip
          className="sd-drawer-project-rule-detail__warning"
          content={
            <div>
              {
                copy.components.projectRuleDetailDrawer
                  .ruleBuilderIncompleteTooltip
              }
              <Pre>
                {localTextTokens ? (
                  <QueryTokens
                    allTokens={localTextTokens.all}
                    ignoredTokens={localTextTokens.ignored}
                    unprocessedTokens={localTextTokens.unprocessed}
                  />
                ) : (
                  localTextValue
                )}
              </Pre>
            </div>
          }
          popoverClassName="sd-drawer-project-rule-detail__tokens"
          position={PopoverPosition.TOP_RIGHT}
        >
          <Callout intent={Intent.WARNING}>
            {
              copy.components.projectRuleDetailDrawer
                .ruleBuilderIncompleteCallout
            }
          </Callout>
        </Tooltip>
      ) : null}

      <div data-testid={TEST_IDS.projectRuleDetailDrawer.ifClause}>
        {copy.components.projectRuleDetailDrawer.ifClause}
        <QueryBuilderSchemaContext.Provider value={IF_QUERY_BUILDER_SCHEMA}>
          <QueryBuilderModelContext.Provider value={validModel}>
            <QueryBuilderContext.Provider value={ifCtx}>
              <TopExpressionBlock setTopExpression={handleSetIf} />
            </QueryBuilderContext.Provider>
          </QueryBuilderModelContext.Provider>
        </QueryBuilderSchemaContext.Provider>
      </div>

      <div data-testid={TEST_IDS.projectRuleDetailDrawer.thenClause}>
        {copy.components.projectRuleDetailDrawer.thenClause}
        <QueryBuilderSchemaContext.Provider value={thenSchema}>
          <QueryBuilderModelContext.Provider value={validThenModel}>
            <QueryBuilderContext.Provider value={thenCtx}>
              <TopExpressionBlock setTopExpression={handleSetThen} />
            </QueryBuilderContext.Provider>
          </QueryBuilderModelContext.Provider>
        </QueryBuilderSchemaContext.Provider>
      </div>

      {showReasoningWarning ? (
        <div data-testid={TEST_IDS.projectRuleDetailDrawer.reasoningWarning}>
          <Callout intent={Intent.PRIMARY}>
            {copy.components.projectRuleDetailDrawer.reasoningWarning}
          </Callout>
        </div>
      ) : null}

      {showValidation ? (
        <div data-testid={TEST_IDS.projectRuleDetailDrawer.error}>
          <Callout intent={Intent.DANGER}>
            {copy.components.projectRuleDetailDrawer.validation.incomplete}
          </Callout>
        </div>
      ) : null}
    </>
  );

  const textEditor = (
    <ProjectRuleTextEditor
      setTextValue={setTextValue}
      textValue={localTextValue}
    />
  );

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.projectRuleDetailDrawer.container}
      >
        <Label className="sd-drawer-editable-label">
          {copy.components.projectRuleDetailDrawer.name}
          <div className={Classes.CALLOUT}>
            <EditableLabel
              key={`${didEditName}`}
              onBlur={handleBlur}
              onSetValue={handleSetName}
              onValidate={handleValidateName}
              placeholder={
                existingRule?.name ||
                copy.components.projectRuleDetailDrawer.newRule
              }
              setValueOnChange
              value={name}
            />
          </div>
        </Label>
        {allModelItems.length > 1 ? (
          <div className="sd-drawer-project-diagram-entity">
            <SelectableLinks
              allowEmpty={false}
              buttonText=""
              filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
              header={copy.components.projectDiagram.entityDrawer.models}
              items={availableModels}
              itemTestId={(value, idx) =>
                getProjectDiagramEntityDrawerSelectLinkTestId(
                  value,
                  'models',
                  idx
                )
              }
              onRemove={handleRemoveModel}
              onSelect={handleAddModel}
              removeDisabledTooltip={
                copy.components.projectDiagram.entityDrawer.modelsRemoveDisabled
              }
              selected={selectedModelSelectItems}
              selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
              selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
                'models'
              )}
            />
          </div>
        ) : null}
        <Divider />

        <Tabs
          animate={false}
          className="sd-drawer-project-rule-detail__tabs"
          onChange={(tabIdx: RuleDetailDrawerTab) => {
            setSelectedTab(tabIdx);
          }}
          renderActiveTabPanelOnly
          selectedTabId={selectedTab}
        >
          <Tab
            disabled={Boolean(ruleBuilderTabTooltip)}
            id={RuleDetailDrawerTab.RULE_BUILDER}
            panel={queryBuilder}
          >
            <Tooltip
              className="sd-drawer-project-rule-detail__tab"
              content={ruleBuilderTabTooltip}
              disabled={!ruleBuilderTabTooltip}
              position={PopoverPosition.BOTTOM}
            >
              <Text>
                {copy.components.projectRuleDetailDrawer.ruleBuilderTabName}
              </Text>
            </Tooltip>
          </Tab>
          {showRuleBuilderWarning ? null : <Icon icon={IconNames.Blank} />}
          <Tab
            disabled={Boolean(textTabTooltip)}
            id={RuleDetailDrawerTab.TEXT_EDITOR}
            panel={textEditor}
          >
            <Tooltip
              content={textTabTooltip}
              disabled={!textTabTooltip}
              position={PopoverPosition.BOTTOM}
            >
              <Text>
                {copy.components.projectRuleDetailDrawer.textEditorTabName}
              </Text>
            </Tooltip>
          </Tab>
        </Tabs>
      </div>
      <div className={classNames(Classes.DRAWER_FOOTER)}>
        <div className="sd-drawer-footer-actions">
          {existingRule ? (
            <Button
              data-testid={TEST_IDS.projectRuleDetailDrawer.deleteButton}
              onClick={handleDelete}
              outlined
              text={copy.components.projectRuleDetailDrawer.delete}
            />
          ) : null}
        </div>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectRuleDetailDrawer.cancelButton}
            intent={Intent.PRIMARY}
            onClick={onClose}
            outlined
            text={copy.components.projectRuleDetailDrawer.cancel}
          />
          <Tooltip content={saveButtonError} disabled={!saveButtonError}>
            <AnchorButton
              data-testid={TEST_IDS.projectRuleDetailDrawer.submitButton}
              disabled={!isSaveEnabled}
              intent={Intent.PRIMARY}
              onClick={onSubmit}
              text={copy.components.projectRuleDetailDrawer.submit}
            />
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export const ProjectRuleDetailDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector((state) => state.ui.ruleDetailDrawer.isOpen);

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-rule-detail"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectRuleDetailDrawer.title}
      usePortal={false}
    >
      <ProjectRuleDetailDrawerBody />
    </Drawer>
  );
};
